import React, { useCallback, useContext } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import Image from 'components/Common/Image'
import ModalContext from 'contexts/ModalContext'
import ImageGalleryLightbox from 'LuxKit/Carousel/ImageGalleryLightbox'

interface GalleryImage {
  id: string
  description?: string
}

interface Props {
  images: Array<GalleryImage>
}

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: auto auto auto auto;
  gap: ${rem(20)};
  width: 100%;
`

const ImageWrapper = styled.div<{ gridArea?: string }>`
  position: relative;
  width: 100%;
  height: 100%;
  opacity: 1;
  transition: opacity 0.2s ease-in-out, transform 0.2s ease-in-out;
  cursor: pointer;

  &:hover {
    transform: scale(1.01);
    opacity: 0.9;
  }

  picture {
    width: 100%;
    height: 100%;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  /* Row 1-2, Col 1: item 1 (tall, spans 2 rows) */
  &.grid-area-1 {
    grid-area: 1 / 1 / 3 / 2;
  }

  /* Row 1, Col 2: item 2 */
  &.grid-area-2 {
    grid-area: 1 / 2 / 2 / 3;
  }
  
  /* Row 2, Col 2: item 3 */
  &.grid-area-4 {
    grid-area: 3 / 1 / 4 / 3;
  }

  /* Row 3, Col 1-2: item 4 (full width) */
  &.grid-area-5 {
    grid-area: 4 / 1 / 5 / 2;
  }
  
  /* Row 4, Col 2: item 6 */
  &.grid-area-6 {
    grid-area: 4 / 2 / 5 / 3;
  }
`

const IMAGES_SIZE: Record<number, { width: number, height: number }> = {
  0: { width: 560, height: 506 },
  1: { width: 560, height: 240 },
  2: { width: 560, height: 240 },
  3: { width: 1140, height: 400 },
  4: { width: 560, height: 240 },
  5: { width: 560, height: 240 },
}

function WeddingGallery({ images }: Props) {
  const showLightbox = useContext(ModalContext)

  const showImageLightbox = useCallback(async(e: React.MouseEvent<HTMLDivElement>) => {
    const index = Number(e.currentTarget.dataset.index) || 0
    await showLightbox(<ImageGalleryLightbox
      images={images}
      defaultIndex={index}
    />)
  }, [images, showLightbox])

  return (
    <Grid>
      {images.slice(0, 6).map((image, index) => (
        <ImageWrapper
          key={image.id}
          className={`grid-area-${index + 1}`}
          onClick={showImageLightbox}
          data-index={index}
        >
          <Image
            id={image.id}
            alt={image.description || ''}
            width={IMAGES_SIZE[index].width}
            height={IMAGES_SIZE[index].height}
            objectFit="cover"
          />
        </ImageWrapper>
      ))}
    </Grid>
  )
}

export default WeddingGallery
